import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '@/services/api';
import { useI18nStore } from '@/store/i18nStore';

/* ─── Types ──────────────────────────────────── */
interface Ticket {
  id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  message_count: number;
  unread_admin_replies: number;
  created_at: string;
  updated_at: string;
  messages?: TicketMessage[];
}

interface TicketMessage {
  id: string;
  sender_id: string;
  sender_email?: string;
  sender_name?: string;
  body: string;
  is_admin: number;
  created_at: string;
}

/* ─── Constants ──────────────────────────────── */
const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-500/10 text-blue-500 dark:text-blue-400',
  in_progress: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
  resolved: 'bg-green-500/10 text-green-600 dark:text-green-400',
  closed: 'bg-surface-200/50 text-surface-500 dark:bg-surface-700/50 dark:text-surface-400',
};
const STATUS_LABELS: Record<string, string> = { open: 'Aperto', in_progress: 'In Corso', resolved: 'Risolto', closed: 'Chiuso' };
const CATEGORY_OPTIONS = [
  { value: 'general', label: 'Generale' },
  { value: 'technical', label: 'Tecnico' },
  { value: 'payment', label: 'Pagamento' },
  { value: 'account', label: 'Account' },
  { value: 'suggestion', label: 'Suggerimento' },
];
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Bassa' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
];

/* ─── Component ──────────────────────────────── */
export default function TicketPage() {
  const { t } = useI18nStore();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [form, setForm] = useState({ subject: '', body: '', category: 'general', priority: 'medium' });

  const { data: listData, isLoading } = useQuery<{ tickets: Ticket[] }>({
    queryKey: ['my-tickets'],
    queryFn: () => api.get('/tickets/').then((r) => r.data),
    refetchInterval: 15_000,
  });

  const { data: detailData } = useQuery<{ ticket: Ticket }>({
    queryKey: ['ticket-detail', selectedId],
    queryFn: () => api.get(`/tickets/${selectedId}`).then((r) => r.data),
    enabled: !!selectedId,
    refetchInterval: 10_000,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof form) => api.post('/tickets/', data),
    onSuccess: () => {
      toast.success('Ticket creato con successo');
      setForm({ subject: '', body: '', category: 'general', priority: 'medium' });
      setShowForm(false);
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
    },
    onError: () => toast.error('Errore creazione ticket'),
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      api.post(`/tickets/${id}/messages`, { body }),
    onSuccess: () => {
      toast.success('Messaggio inviato');
      setReplyText('');
      qc.invalidateQueries({ queryKey: ['ticket-detail', selectedId] });
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
    },
    onError: () => toast.error('Errore invio messaggio'),
  });

  const tickets = listData?.tickets ?? [];
  const detail = detailData?.ticket;

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ora';
    if (mins < 60) return `${mins}m fa`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h fa`;
    return `${Math.floor(hours / 24)}g fa`;
  };

  return (
    <>
      <Helmet><title>{t('nav.tickets', { defaultValue: 'Ticket' })} — React-City</title></Helmet>

      <div className="space-y-6 animate-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-surface-900 dark:text-white">🎫 Supporto</h1>
            <p className="text-sm text-surface-500 dark:text-surface-400 mt-1">
              Apri un ticket per contattare l'assistenza
            </p>
          </div>
          <button
            onClick={() => { setShowForm(!showForm); setSelectedId(null); }}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
          >
            + Nuovo Ticket
          </button>
        </div>

        {/* Create form */}
        {showForm && (
          <div className="bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-700 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-surface-900 dark:text-white">Nuovo Ticket</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-surface-500 dark:text-surface-400 mb-1">Categoria</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:outline-none"
                >
                  {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-surface-500 dark:text-surface-400 mb-1">Priorità</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:outline-none"
                >
                  {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-500 dark:text-surface-400 mb-1">Oggetto</label>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder="Descrivi brevemente il problema"
                className="w-full px-3 py-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-500 dark:text-surface-400 mb-1">Messaggio</label>
              <textarea
                rows={4}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Descrivici il tuo problema in dettaglio…"
                className="w-full px-3 py-2 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:outline-none resize-none"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors">
                Annulla
              </button>
              <button
                onClick={() => createMutation.mutate(form)}
                disabled={!form.subject || !form.body || createMutation.isPending}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {createMutation.isPending ? 'Creazione…' : 'Crea Ticket'}
              </button>
            </div>
          </div>
        )}

        {/* Ticket list + detail */}
        <div className="flex gap-4 h-[calc(100vh-22rem)]">
          {/* List */}
          <div className="w-80 flex-shrink-0 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-700 flex flex-col overflow-hidden">
            <div className="p-3 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-sm font-semibold text-surface-900 dark:text-white">
                I miei ticket ({tickets.length})
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-surface-400 animate-pulse text-sm">Caricamento…</div>
              ) : tickets.length === 0 ? (
                <div className="p-6 text-center text-surface-400 text-sm">
                  <p className="mb-2">Nessun ticket</p>
                  <p className="text-xs text-surface-400/70">Crea un ticket per contattare l'assistenza</p>
                </div>
              ) : (
                tickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setSelectedId(t.id); setShowForm(false); }}
                    className={`w-full p-3 text-left border-b border-surface-50 dark:border-surface-800 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors ${
                      selectedId === t.id ? 'bg-primary-50/50 dark:bg-primary-500/5' : ''
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`inline-flex px-1.5 py-0.5 text-[10px] rounded-full font-medium ${STATUS_COLORS[t.status] ?? STATUS_COLORS.open}`}>
                        {STATUS_LABELS[t.status] ?? t.status}
                      </span>
                      {t.unread_admin_replies > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                          {t.unread_admin_replies}
                        </span>
                      )}
                      <span className="text-[10px] text-surface-400 ml-auto">{timeAgo(t.updated_at)}</span>
                    </div>
                    <p className="text-sm font-medium text-surface-900 dark:text-white truncate">{t.subject}</p>
                    <p className="text-xs text-surface-400 mt-0.5">{t.message_count} messaggi</p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="flex-1 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-700 flex flex-col overflow-hidden">
            {!selectedId ? (
              <div className="flex-1 flex items-center justify-center text-surface-400 text-sm">
                Seleziona un ticket per visualizzare i messaggi
              </div>
            ) : !detail ? (
              <div className="flex-1 flex items-center justify-center text-surface-400 animate-pulse">Caricamento…</div>
            ) : (
              <>
                {/* Header */}
                <div className="p-4 border-b border-surface-100 dark:border-surface-800">
                  <h3 className="font-semibold text-surface-900 dark:text-white">{detail.subject}</h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-surface-400">
                    <span className={`inline-flex px-1.5 py-0.5 rounded-full ${STATUS_COLORS[detail.status]}`}>
                      {STATUS_LABELS[detail.status]}
                    </span>
                    <span>·</span>
                    <span>{CATEGORY_OPTIONS.find((c) => c.value === detail.category)?.label ?? detail.category}</span>
                    <span>·</span>
                    <span>{PRIORITY_OPTIONS.find((p) => p.value === detail.priority)?.label ?? detail.priority}</span>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {detail.messages?.map((msg) => (
                    <div key={msg.id} className={`max-w-[80%] ${msg.is_admin ? 'ml-auto' : 'mr-auto'}`}>
                      <div className={`rounded-2xl px-4 py-2.5 text-sm ${
                        msg.is_admin
                          ? 'bg-primary-600 text-white'
                          : 'bg-surface-100 dark:bg-surface-800 text-surface-900 dark:text-white'
                      }`}>
                        {msg.body}
                      </div>
                      <p className={`text-[10px] text-surface-400 mt-1 ${msg.is_admin ? 'text-right' : ''}`}>
                        {msg.is_admin ? '👨‍💼 Supporto' : '👤 Tu'} · {new Date(msg.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Reply */}
                {!['closed', 'resolved'].includes(detail.status) ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (replyText.trim() && selectedId) {
                        replyMutation.mutate({ id: selectedId, body: replyText.trim() });
                      }
                    }}
                    className="p-3 border-t border-surface-100 dark:border-surface-800 flex gap-2"
                  >
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Scrivi un messaggio…"
                      className="flex-1 px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-sm text-surface-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:outline-none"
                    />
                    <button
                      type="submit"
                      disabled={!replyText.trim() || replyMutation.isPending}
                      className="px-4 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      Invia
                    </button>
                  </form>
                ) : (
                  <div className="p-3 border-t border-surface-100 dark:border-surface-800 text-center text-xs text-surface-400">
                    Questo ticket è {STATUS_LABELS[detail.status]?.toLowerCase()} e non accetta nuove risposte
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
